import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, X, Upload, Image as ImageIcon, Star, ExternalLink, GripVertical } from "lucide-react";

const BLANK = {
  title: "",
  slug: "",
  category: "Extension",
  location: "",
  area_slug: "",
  service_slug: "",
  year: new Date().getFullYear().toString(),
  img: "",
  description: "",
  overview: "",
  scope_of_works: [],
  materials_used: [],
  challenges_and_solutions: "",
  duration_weeks: "",
  client_testimonial: "",
  client_testimonial_author: "",
  gallery: [],
  lat: "",
  lng: "",
  display_order: 0,
  published: true,
  featured: false,
};

const AREA_OPTIONS = [
  { slug: "", label: "— none —" },
  { slug: "wembley", label: "Wembley" },
  { slug: "ealing", label: "Ealing" },
  { slug: "richmond", label: "Richmond" },
  { slug: "chiswick", label: "Chiswick" },
  { slug: "wimbledon", label: "Wimbledon" },
  { slug: "hampstead", label: "Hampstead" },
  { slug: "croydon", label: "Croydon" },
];

const SERVICE_OPTIONS = [
  { slug: "", label: "— none —" },
  { slug: "house-extensions-london", label: "House Extensions" },
  { slug: "loft-conversions-london", label: "Loft Conversions" },
  { slug: "kitchen-renovations-london", label: "Kitchen Renovations" },
  { slug: "bathroom-renovations-london", label: "Bathroom Renovations" },
  { slug: "full-house-renovations-london", label: "Full House Renovations" },
  { slug: "new-build-contractors-london", label: "New Build Contractors" },
  { slug: "structural-work-london", label: "Structural Work" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (img) => (img && img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img);

export default function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryPhase, setGalleryPhase] = useState("after");
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects/admin/all");
      setItems(data);
    } catch {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (i) => setEditing({ ...BLANK, ...i, scope_of_works: i.scope_of_works || [], materials_used: i.materials_used || [], gallery: i.gallery || [] });
  const cancel = () => setEditing(null);

  const onHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((cur) => ({ ...cur, img: data.url }));
      toast.success("Hero image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setGalleryUploading(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, phase: galleryPhase, caption: "" });
      }
      setEditing((cur) => ({ ...cur, gallery: [...(cur.gallery || []), ...added] }));
      toast.success(`${added.length} image${added.length === 1 ? "" : "s"} added to ${galleryPhase} gallery`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setGalleryUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const updateGalleryItem = (idx, patch) => {
    setEditing((cur) => {
      const next = [...(cur.gallery || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...cur, gallery: next };
    });
  };

  const removeGalleryItem = (idx) => {
    setEditing((cur) => ({ ...cur, gallery: (cur.gallery || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!editing.title || !editing.location || !editing.img) {
      toast.error("Title, location and hero image are required");
      return;
    }
    setSaving(true);
    try {
      const clean = (arr) => (Array.isArray(arr) ? arr.map((s) => (typeof s === "string" ? s.trim() : s)).filter(Boolean) : []);
      const payload = {
        title: editing.title,
        slug: editing.slug || undefined,
        category: editing.category,
        location: editing.location,
        area_slug: editing.area_slug || undefined,
        service_slug: editing.service_slug || undefined,
        year: editing.year || new Date().getFullYear().toString(),
        img: editing.img,
        description: editing.description || undefined,
        overview: editing.overview || undefined,
        scope_of_works: clean(editing.scope_of_works),
        materials_used: clean(editing.materials_used),
        challenges_and_solutions: editing.challenges_and_solutions || undefined,
        duration_weeks: editing.duration_weeks ? Number(editing.duration_weeks) : undefined,
        client_testimonial: editing.client_testimonial || undefined,
        client_testimonial_author: editing.client_testimonial_author || undefined,
        gallery: (editing.gallery || []).filter((g) => g.url),
        lat: editing.lat !== "" && editing.lat !== null ? Number(editing.lat) : undefined,
        lng: editing.lng !== "" && editing.lng !== null ? Number(editing.lng) : undefined,
        display_order: Number(editing.display_order || 0),
        published: !!editing.published,
        featured: !!editing.featured,
      };
      if (editing.id) {
        await api.patch(`/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const { data } = await api.patch(`/projects/${item.id}`, { featured: !item.featured });
      setItems((arr) => arr.map((i) => (i.id === item.id ? data : i)));
      toast.success(data.featured ? "Featured on homepage" : "Un-featured");
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex justify-between items-end flex-wrap gap-4 mb-6">
        <div>
          <div className="overline">Portfolio</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Projects</h2>
          <p className="text-sm text-slate-500 mt-1">Rich project pages with before/during/after galleries, scope, materials, and homepage feature toggle.</p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-accent text-sm" data-testid="admin-project-new">
            <Plus size={14} /> New project
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-white border border-slate-200 p-6 mb-8 space-y-8" data-testid="admin-project-form">
          <div className="overline">{editing.id ? "Edit project" : "New project"}</div>

          {/* Basics */}
          <Section title="Basics">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="p-title" />
              <Field label="Slug (URL — optional)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="p-slug" placeholder="auto-generated from title" />
              <div>
                <label className="overline">Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="field mt-2" data-testid="p-category">
                  <option>New Build</option>
                  <option>Renovation</option>
                  <option>Extension</option>
                </select>
              </div>
              <div>
                <label className="overline">Service (deep link)</label>
                <select value={editing.service_slug} onChange={(e) => setEditing({ ...editing, service_slug: e.target.value })} className="field mt-2" data-testid="p-service">
                  {SERVICE_OPTIONS.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                </select>
              </div>
              <Field label="Location (display text)" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} placeholder="e.g. Ealing, W5" testid="p-location" />
              <div>
                <label className="overline">Area (deep link)</label>
                <select value={editing.area_slug} onChange={(e) => setEditing({ ...editing, area_slug: e.target.value })} className="field mt-2" data-testid="p-area">
                  {AREA_OPTIONS.map((a) => <option key={a.slug} value={a.slug}>{a.label}</option>)}
                </select>
              </div>
              <Field label="Year" value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} testid="p-year" />
              <Field label="Duration (weeks)" type="number" value={editing.duration_weeks} onChange={(v) => setEditing({ ...editing, duration_weeks: v })} testid="p-duration" placeholder="e.g. 22" />
              <Field label="Display order (lower = first)" type="number" value={editing.display_order} onChange={(v) => setEditing({ ...editing, display_order: v })} testid="p-order" />
              <div className="flex items-end gap-6">
                <label className="flex items-center gap-2 text-sm text-slate-700 mb-2">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="p-published" />
                  Published
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 mb-2">
                  <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="p-featured" />
                  Featured on homepage
                </label>
              </div>
            </div>
          </Section>

          {/* Hero */}
          <Section title="Hero image">
            <div className="mt-2 flex flex-col md:flex-row gap-4 items-start">
              {editing.img ? (
                <img src={resolveImg(editing.img)} alt="Preview" className="w-56 h-36 object-cover border border-slate-200" />
              ) : (
                <div className="w-56 h-36 border border-dashed border-slate-300 flex items-center justify-center text-slate-400 bg-slate-50">
                  <ImageIcon size={22} />
                </div>
              )}
              <div className="flex-1 space-y-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onHeroUpload}
                  disabled={uploading}
                  data-testid="p-image-upload"
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer file:hover:bg-slate-800"
                />
                <input className="field" placeholder="…or paste an image URL" value={editing.img} onChange={(e) => setEditing({ ...editing, img: e.target.value })} data-testid="p-image-url" />
                {uploading && <div className="text-xs text-slate-500"><Upload size={12} className="inline" /> Uploading…</div>}
              </div>
            </div>
          </Section>

          {/* Long-form */}
          <Section title="Story">
            <div>
              <label className="overline">Overview (2-3 sentences shown in the hero + snippet)</label>
              <textarea className="field mt-2" rows={3} value={editing.overview || ""} onChange={(e) => setEditing({ ...editing, overview: e.target.value })} data-testid="p-overview" />
            </div>
            <ListEditor
              label="Scope of works (one item per line)"
              items={editing.scope_of_works}
              onChange={(v) => setEditing({ ...editing, scope_of_works: v })}
              testid="p-scope"
              placeholder={"Rear structural opening with 4m steel goalpost\nBespoke kitchen with book-matched marble worktop"}
            />
            <ListEditor
              label="Materials used (chips shown as small labels)"
              items={editing.materials_used}
              onChange={(v) => setEditing({ ...editing, materials_used: v })}
              testid="p-materials"
              placeholder={"Book-matched Carrara marble\nBrushed brass fittings\nEngineered oak"}
            />
            <div>
              <label className="overline">Challenges & solutions</label>
              <textarea className="field mt-2" rows={4} value={editing.challenges_and_solutions || ""} onChange={(e) => setEditing({ ...editing, challenges_and_solutions: e.target.value })} data-testid="p-challenges" />
            </div>
          </Section>

          {/* Testimonial */}
          <Section title="Client testimonial (optional)">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="overline">Quote</label>
                <textarea className="field mt-2" rows={3} value={editing.client_testimonial || ""} onChange={(e) => setEditing({ ...editing, client_testimonial: e.target.value })} data-testid="p-testimonial" />
              </div>
              <Field label="Author (e.g. 'Recent client · Richmond TW10')" value={editing.client_testimonial_author} onChange={(v) => setEditing({ ...editing, client_testimonial_author: v })} testid="p-testimonial-author" />
            </div>
          </Section>

          {/* Map coordinates */}
          <Section title="Map location (optional)">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Latitude" value={editing.lat} onChange={(v) => setEditing({ ...editing, lat: v })} testid="p-lat" placeholder="51.5130" />
              <Field label="Longitude" value={editing.lng} onChange={(v) => setEditing({ ...editing, lng: v })} testid="p-lng" placeholder="-0.3050" />
            </div>
            <p className="text-xs text-slate-500 mt-2">Tip: leave blank and the map will use the area centroid. Right-click a location on Google Maps → the coordinates appear at the top of the menu.</p>
          </Section>

          {/* Gallery */}
          <Section title="Gallery (Before / During / After)">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="overline">Adding to</span>
              {["before", "during", "after"].map((ph) => (
                <button
                  key={ph}
                  type="button"
                  onClick={() => setGalleryPhase(ph)}
                  data-testid={`p-gallery-phase-${ph}`}
                  className={`px-3 h-9 text-xs font-medium tracking-[0.16em] uppercase border transition ${galleryPhase === ph ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                >
                  {ph}
                </button>
              ))}
              <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                multiple
                onChange={onGalleryUpload}
                disabled={galleryUploading}
                data-testid="p-gallery-upload"
                className="text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#C5A059] file:text-white file:cursor-pointer file:hover:opacity-90"
              />
              {galleryUploading && <span className="text-xs text-slate-500"><Upload size={12} className="inline" /> Uploading…</span>}
            </div>
            {(editing.gallery || []).length === 0 ? (
              <div className="text-sm text-slate-500 border border-dashed border-slate-300 p-8 text-center">No gallery images yet.</div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {editing.gallery.map((g, i) => (
                  <div key={i} className="border border-slate-200 p-3 bg-slate-50" data-testid={`p-gallery-item-${i}`}>
                    <img src={resolveImg(g.url)} alt={g.caption || "gallery"} className="w-full h-32 object-cover border border-slate-200" />
                    <div className="mt-3 grid grid-cols-3 gap-1">
                      {["before", "during", "after"].map((ph) => (
                        <button
                          key={ph}
                          type="button"
                          onClick={() => updateGalleryItem(i, { phase: ph })}
                          className={`text-[10px] tracking-[0.15em] uppercase border px-1 py-1 ${g.phase === ph ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
                        >
                          {ph}
                        </button>
                      ))}
                    </div>
                    <input
                      className="field mt-2 text-sm"
                      placeholder="Caption (optional)"
                      value={g.caption || ""}
                      onChange={(e) => updateGalleryItem(i, { caption: e.target.value })}
                    />
                    <button type="button" onClick={() => removeGalleryItem(i)} className="mt-2 text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`p-gallery-remove-${i}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="admin-project-save">
              <Save size={14} /> {saving ? "Saving…" : "Save project"}
            </button>
            <button onClick={cancel} className="btn-ghost text-sm" data-testid="admin-project-cancel">
              <X size={14} /> Cancel
            </button>
            {editing.id && editing.published && (
              <a href={`/projects/${editing.slug || editing.id}`} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="admin-project-view">
                <ExternalLink size={14} /> View live
              </a>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm" data-testid="projects-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <th className="p-4 overline">Project</th>
              <th className="p-4 overline">Category</th>
              <th className="p-4 overline">Year</th>
              <th className="p-4 overline">Featured</th>
              <th className="p-4 overline">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-slate-500">No admin projects yet — add one to surface it on the site.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => startEdit(p)} data-testid={`project-row-${p.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={resolveImg(p.img)} alt="" className="w-14 h-10 object-cover border border-slate-200" loading="lazy" />
                    <div>
                      <div className="font-medium text-slate-900">{p.title}</div>
                      <div className="text-xs text-slate-500">/{p.slug || p.id} · {p.location}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">{p.category}</td>
                <td className="p-4 text-slate-600">{p.year}</td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => toggleFeatured(p)}
                    aria-label="Toggle featured"
                    className="text-slate-400 hover:text-[#C5A059]"
                    data-testid={`project-featured-${p.id}`}
                  >
                    <Star size={16} className={p.featured ? "text-[#C5A059] fill-[#C5A059]" : ""} />
                  </button>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-[10px] font-medium tracking-wide border ${p.published ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-100 border-slate-300 text-slate-700"}`}>
                    {p.published ? "PUBLISHED" : "DRAFT"}
                  </span>
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => remove(p.id)} aria-label="Delete" className="text-slate-400 hover:text-red-600" data-testid={`project-delete-${p.id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pb-6 border-b border-slate-200 last:border-b-0 last:pb-0 space-y-4">
      <div className="flex items-center gap-2">
        <GripVertical size={14} className="text-slate-300" />
        <div className="font-display text-sm font-semibold tracking-wide uppercase text-slate-500">{title}</div>
      </div>
      {children}
    </div>
  );
}

function ListEditor({ label, items = [], onChange, testid, placeholder }) {
  const value = (items || []).join("\n");
  return (
    <div>
      <label className="overline">{label}</label>
      <textarea
        className="field mt-2"
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value.split(/\r?\n/))}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="field mt-2"
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
